/* eslint-disable */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import { IDetalheOrdemServico } from '../../../shared/services/api/servicos/OrdemServicoService';

// Mesmo carregamento de logo (com fallback gerado em canvas) já usado nos
// outros comprovantes desta sessão (impressaocarne.ts/ComprovanteBaixa.ts)
// — repetido em vez de importado porque cada gerador de PDF do projeto já
// duplica essa função (mesmo padrão de sempre).
async function getBase64ImageFromUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Erro canvas');
      canvas.height = img.naturalHeight;
      canvas.width = img.naturalWidth;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject('Erro ao carregar logo');
    img.src = url;
  });
}

function gerarLogoPadrao(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 90;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const gradient = ctx.createLinearGradient(0, 0, 320, 0);
  gradient.addColorStop(0, '#1f2937');
  gradient.addColorStop(1, '#374151');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.beginPath();
  ctx.arc(45, 45, 25, 0, 2 * Math.PI);
  ctx.fillStyle = '#10b981';
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 20px Arial';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('OS', 90, 55);
  return canvas.toDataURL('image/png');
}

const formatarMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarData = (data: unknown) => (data ? moment(data as string).format('DD/MM/YYYY') : '—');

// Comprovante final da OS — só faz sentido depois de CONCLUIDA (ver botão
// "Imprimir Comprovante" em DetalheDeOrdemServico.tsx, só habilitado nesse
// status). Mesmo estilo visual dos outros comprovantes desta sessão: logo +
// nome da empresa, faixa de destaque no topo, sem tabelas com fundo
// colorido pesado.
export async function gerarComprovanteOS(
  os: IDetalheOrdemServico,
  empresaFantasia?: string,
  empresaRazaoSocial?: string,
  logo?: string,
): Promise<Blob> {
  // Precisa ser setado aqui dentro (não só uma vez no topo do módulo) e com
  // esse caminho exato (`pdfFonts.pdfMake.vfs`, não `pdfFonts.vfs`) — é como
  // o pacote `pdfmake` 0.2.7 realmente expõe o vfs nessa versão (ver
  // impressaovenda01.ts, que já fazia certo). Setar errado deixa
  // `pdfMake.vfs` undefined e quebra qualquer `bold: true` com "File
  // 'Roboto-Medium.ttf' not found in virtual file system".
  (pdfMake as any).fonts = {
    Roboto: {
      normal: 'Roboto-Regular.ttf',
      bold: 'Roboto-Medium.ttf',
      italics: 'Roboto-Italic.ttf',
      bolditalics: 'Roboto-MediumItalic.ttf',
    },
  };
  if ((pdfFonts as any)?.pdfMake?.vfs) {
    (pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
  }

  let imageData: string;
  if (logo && logo.trim() !== '') {
    const baseUrl = (window as any).env?.REACT_APP_URL_IMGLOG || '';
    try {
      imageData = await getBase64ImageFromUrl(baseUrl + logo);
    } catch {
      imageData = gerarLogoPadrao();
    }
  } else {
    imageData = gerarLogoPadrao();
  }

  const nomeEmpresa = empresaFantasia || empresaRazaoSocial || '';

  const documentDefinition: any = {
    pageSize: 'A4',
    pageMargins: [40, 30, 40, 40],

    content: [
      {
        canvas: [{ type: 'rect', x: 0, y: 0, w: 515, h: 4, color: '#1f2937' }],
        margin: [0, 0, 0, 14],
      },
      {
        columns: [
          { image: imageData, width: 46, alignment: 'left' },
          {
            width: '*',
            stack: [
              ...(nomeEmpresa ? [{ text: nomeEmpresa, bold: true, fontSize: 12 }] : []),
              ...(empresaRazaoSocial && empresaRazaoSocial !== nomeEmpresa
                ? [{ text: empresaRazaoSocial, fontSize: 7, color: '#6b7280' }]
                : []),
            ],
            margin: [8, 2, 0, 0],
          },
          {
            width: 190,
            stack: [
              { text: `ORDEM DE SERVIÇO Nº ${os.numero}`, bold: true, fontSize: 12, alignment: 'right' },
              { text: `Emissão: ${formatarData(os.emissao)}`, fontSize: 8, alignment: 'right', color: '#6b7280' },
            ],
          },
        ],
        columnGap: 8,
        margin: [0, 0, 0, 10],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#e5e7eb' }], margin: [0, 0, 0, 12] },

      {
        columns: [
          {
            width: '*',
            stack: [
              { text: [{ text: 'Cliente: ', bold: true }, os.nomeparceiro], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'CPF/CNPJ: ', bold: true }, os.cpfparceiro || '—'], fontSize: 9 },
            ],
          },
          {
            width: '*',
            stack: [
              {
                text: [
                  { text: 'Bem: ', bold: true },
                  `${os.descricaobem}${os.marca || os.modelo ? ` — ${[os.marca, os.modelo].filter(Boolean).join(' ')}` : ''}`,
                ],
                fontSize: 9,
                margin: [0, 0, 0, 3],
              },
              { text: [{ text: 'Placa/Patrimônio: ', bold: true }, os.placa || os.patrimonio || '—'], fontSize: 9 },
            ],
          },
        ],
        columnGap: 12,
        margin: [0, 0, 0, 12],
      },

      { text: 'RELATO DO CLIENTE', bold: true, fontSize: 9, color: '#374151', margin: [0, 0, 0, 3] },
      { text: os.relatocliente || '—', fontSize: 9, margin: [0, 0, 0, 10] },

      { text: 'DEFEITO CONSTATADO', bold: true, fontSize: 9, color: '#374151', margin: [0, 0, 0, 3] },
      { text: os.defeitoconstatado || '—', fontSize: 9, margin: [0, 0, 0, 12] },

      { text: 'PEÇAS E PRODUTOS', bold: true, fontSize: 9, color: '#374151', margin: [0, 0, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: ['*', 40, 40, 60, 50, 60],
          body: [
            [
              { text: 'Descrição', bold: true, fontSize: 8 },
              { text: 'Un', bold: true, fontSize: 8, alignment: 'center' },
              { text: 'Qtd', bold: true, fontSize: 8, alignment: 'right' },
              { text: 'Vl. Unit.', bold: true, fontSize: 8, alignment: 'right' },
              { text: 'Desc.', bold: true, fontSize: 8, alignment: 'right' },
              { text: 'Total', bold: true, fontSize: 8, alignment: 'right' },
            ],
            ...(os.itens.length > 0 ? os.itens.map((item) => [
              { text: item.descricao, fontSize: 8 },
              { text: item.unidade || '-', fontSize: 8, alignment: 'center' },
              { text: String(item.quantidade), fontSize: 8, alignment: 'right' },
              { text: formatarMoeda(item.valorunitario), fontSize: 8, alignment: 'right' },
              { text: formatarMoeda(item.desconto), fontSize: 8, alignment: 'right' },
              { text: formatarMoeda(item.total), fontSize: 8, alignment: 'right' },
            ]) : [[{ text: 'Nenhuma peça/produto.', fontSize: 8, colSpan: 6, color: '#6b7280' }, {}, {}, {}, {}, {}]]),
            [
              { text: 'Subtotal peças', fontSize: 8, colSpan: 5, bold: true },
              {}, {}, {}, {},
              { text: formatarMoeda(os.vprodutos), fontSize: 8, alignment: 'right', bold: true },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
          vLineWidth: () => 0,
          hLineColor: () => '#d1d5db',
        },
        margin: [0, 0, 0, 10],
      },

      { text: 'MÃO DE OBRA', bold: true, fontSize: 9, color: '#374151', margin: [0, 0, 0, 4] },
      {
        table: {
          headerRows: 1,
          widths: [110, '*', 80],
          body: [
            [
              { text: 'Mecânico', bold: true, fontSize: 8 },
              { text: 'Serviço Realizado', bold: true, fontSize: 8 },
              { text: 'Valor', bold: true, fontSize: 8, alignment: 'right' },
            ],
            ...(os.maodeobra.length > 0 ? os.maodeobra.map((item) => [
              { text: item.mecanico, fontSize: 8 },
              { text: item.descricao, fontSize: 8 },
              { text: formatarMoeda(item.valor), fontSize: 8, alignment: 'right' },
            ]) : [[{ text: 'Nenhuma mão de obra.', fontSize: 8, colSpan: 3, color: '#6b7280' }, {}, {}]]),
            [
              { text: 'Subtotal mão de obra', fontSize: 8, colSpan: 2, bold: true },
              {},
              { text: formatarMoeda(os.vservicos), fontSize: 8, alignment: 'right', bold: true },
            ],
          ],
        },
        layout: {
          hLineWidth: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
          vLineWidth: () => 0,
          hLineColor: () => '#d1d5db',
        },
        margin: [0, 0, 0, 10],
      },

      {
        columns: [
          { text: '', width: '*' },
          {
            width: 200,
            stack: [
              { text: [{ text: 'Peças: ', bold: true }, formatarMoeda(os.vprodutos)], fontSize: 9, alignment: 'right' },
              { text: [{ text: 'Mão de obra: ', bold: true }, formatarMoeda(os.vservicos)], fontSize: 9, alignment: 'right' },
              { text: `TOTAL: ${formatarMoeda(os.total)}`, bold: true, fontSize: 13, alignment: 'right', margin: [0, 4, 0, 0] },
            ],
          },
        ],
        margin: [0, 0, 0, 30],
      },

      {
        columns: [
          {
            width: '*',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 220, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }], margin: [0, 14, 0, 2] },
              { text: 'Assinatura do Cliente', fontSize: 7, color: '#6b7280' },
            ],
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 220, y2: 0, lineWidth: 0.5, lineColor: '#9ca3af' }], margin: [0, 14, 0, 2], alignment: 'right' },
              { text: 'Assinatura da Empresa', fontSize: 7, color: '#6b7280', alignment: 'right' },
            ],
          },
        ],
        margin: [0, 0, 0, 16],
      },

      { text: 'Comprovante referente aos serviços executados na Ordem de Serviço acima — não é documento fiscal.', alignment: 'center', fontSize: 8, color: '#6b7280' },
      { text: `Gerado em ${moment().format('DD/MM/YYYY HH:mm')}`, alignment: 'right', fontSize: 8, margin: [0, 6, 0, 0], color: '#6b7280' },
    ],
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob: Blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
}
